import { useI18n } from "../../lib/i18n.jsx";
import { ACTIVITIES } from "../../lib/content.js";
import { Sun } from "lucide-react";
import Reveal from "../Reveal.jsx";

const ACTIVITY_PHOTOS = {
  ride: "/img/activity/ride.webp",
  tramp: "/img/activity/tramp.webp",
  wall: "/img/activity/wall.webp",
  trampolines: "/img/activity/trampolines.webp",
  water: "/img/activity/water.webp",
  spa: "/img/activity/spa.webp",
  contest: "/img/activity/contest.webp",
  service: "/img/activity/service.webp",
  cinema: "/img/activity/cinema.webp",
  grill: "/img/activity/grill.webp",
  ping: "/img/activity/ping.webp",
  art: "/img/activity/art.webp"
};

export default function Activities() {
  const { t, lang } = useI18n();

  return (
    <section
      id="programme"
      data-testid="activities-section"
      className="bg-cream py-20 md:py-28"
    >
      <div className="max-w-[1300px] mx-auto px-5 md:px-8">
        <Reveal as="div" className="text-center max-w-2xl mx-auto mb-12">
          <p className="font-handwritten text-summer text-2xl md:text-3xl mb-2">
            {t.activities.kicker}
          </p>
          <h2
            className="font-display font-bold text-winter leading-tight mb-4"
            style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}
          >
            {t.activities.title}
          </h2>
          <p className="text-lg text-winter/65">{t.activities.subtitle}</p>
        </Reveal>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {ACTIVITIES.map((a, idx) => {
            const label = lang === "uk" ? (a.title_uk || a.title_en) : lang === "en" ? a.title_en : a.title;
            const photo = ACTIVITY_PHOTOS[a.id];
            return (
              <Reveal
                key={a.id}
                variant="scale"
                delay={Math.min(idx * 60, 480)}
                data-testid={`activity-${a.id}`}
                className="card-soft overflow-hidden group relative"
              >
                {a.summerOnly && (
                  <span
                    className="absolute top-3 right-3 z-10 sticker bg-summer text-cream text-[10px] tracking-wider shadow"
                    title={{ pl: "Tylko obóz letni", uk: "Тільки літній табір", en: "Summer only" }[lang]}
                  >
                    <Sun className="w-3 h-3" /> {{ pl: "Lato", uk: "Літо", en: "Summer" }[lang]}
                  </span>
                )}
                {photo ? (
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={photo}
                      alt={label}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                ) : (
                  <div className="aspect-[4/3] bg-summer/10 flex items-center justify-center">
                    <span className="font-display font-bold text-summer text-5xl">
                      {a.emoji}
                    </span>
                  </div>
                )}
                <div className="p-4 md:p-5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-winter/40 mb-1">
                    #{a.emoji}
                  </p>
                  <h3 className="font-display font-bold text-winter text-lg leading-tight">
                    {label}
                  </h3>
                </div>
              </Reveal>
            );
          })}
        </div>

        <p className="mt-8 text-center text-sm text-winter/55 italic">
          {{
            pl: 'Atrakcje oznaczone "Lato" dostępne wyłącznie podczas obozu wakacyjnego.',
            uk: 'Атракції позначені "Літо" доступні лише під час літнього табору.',
            en: 'Activities marked "Summer" are only available during summer camps.',
          }[lang]}
        </p>
      </div>
    </section>
  );
}
